import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays, startOfDay } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { getGA4Report, getGA4RealTimeUsers, checkGA4Status } from '@/services/ga4Api';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Select from '@/components/common/Select';
import MetricCard from '@/components/charts/MetricCard';
import { formatNumber } from '@/utils/formatters';
import {
  UsersIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import type { GA4DateRange, GA4ReportResponse, GA4DetailedGeoData } from '@/types/ga4';

// Colors for charts
const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function GoogleAnalytics() {
  const [dateRange, setDateRange] = useState<GA4DateRange>('30');

  // Check if GA4 is configured
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['ga4-status'],
    queryFn: checkGA4Status,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch GA4 report data
  const { data: reportData, isLoading: reportLoading, error: reportError, refetch } = useQuery({
    queryKey: ['ga4-report', dateRange],
    queryFn: () => getGA4Report(dateRange),
    enabled: statusData?.configured === true,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch real-time users separately (more frequent updates)
  const { data: realTimeUsers } = useQuery({
    queryKey: ['ga4-realtime'],
    queryFn: getGA4RealTimeUsers,
    enabled: statusData?.configured === true,
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });

  const dateRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '14', label: 'Last 14 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
  ];

  // Format duration in seconds to readable format
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Format percentage
  const formatPercent = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // If GA4 is not configured, show setup instructions
  if (!statusLoading && !statusData?.configured) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Google Analytics</h1>
            <p className="mt-1 text-sm text-gray-500">
              Website traffic and user behavior analytics
            </p>
          </div>
        </div>

        <Card>
          <CardBody>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ExclamationTriangleIcon className="w-16 h-16 text-yellow-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Google Analytics Not Configured
              </h2>
              <p className="text-gray-500 max-w-md mb-6">
                To view analytics data, you need to set up a Google Cloud service account
                and configure the GA4 Data API credentials.
              </p>
              <div className="bg-gray-50 rounded-lg p-6 text-left max-w-2xl w-full">
                <h3 className="font-semibold text-gray-900 mb-3">Setup Instructions:</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                  <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
                  <li>Enable the "Google Analytics Data API"</li>
                  <li>Create a Service Account with "Viewer" role</li>
                  <li>Download the JSON key file</li>
                  <li>In Google Analytics, go to Admin → Property Access Management</li>
                  <li>Add the service account email with "Viewer" permission</li>
                  <li>Set the following Supabase secrets:
                    <pre className="bg-gray-100 p-2 rounded mt-2 text-xs overflow-x-auto">
{`supabase secrets set GA4_PROPERTY_ID=<your-property-id>
supabase secrets set GA4_SERVICE_ACCOUNT_JSON=<base64-encoded-json>`}
                    </pre>
                  </li>
                </ol>
              </div>
              {statusData?.error && (
                <p className="mt-4 text-sm text-red-500">
                  Error: {statusData.error}
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const isLoading = statusLoading || reportLoading;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Website traffic and user behavior analytics
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh data"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
          <Select
            value={dateRange}
            onChange={(value) => setDateRange(value as GA4DateRange)}
            options={dateRangeOptions}
            className="w-40"
          />
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Active Users Now"
          value={formatNumber(realTimeUsers ?? reportData?.realTimeActiveUsers ?? 0)}
          icon={<UsersIcon className="w-5 h-5" />}
          changeLabel="real-time"
          loading={isLoading}
        />
        <MetricCard
          title="Total Sessions"
          value={formatNumber(reportData?.overview?.totalSessions ?? 0)}
          icon={<ArrowTrendingUpIcon className="w-5 h-5" />}
          changeLabel={`in last ${dateRange} days`}
          loading={isLoading}
        />
        <MetricCard
          title="Avg. Session Duration"
          value={formatDuration(reportData?.overview?.avgSessionDuration ?? 0)}
          icon={<ClockIcon className="w-5 h-5" />}
          loading={isLoading}
        />
        <MetricCard
          title="Bounce Rate"
          value={formatPercent(reportData?.overview?.bounceRate ?? 0)}
          icon={<GlobeAltIcon className="w-5 h-5" />}
          loading={isLoading}
        />
      </div>

      {/* Sessions Over Time Chart */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Sessions & Users Over Time</h2>
        </CardHeader>
        <CardBody>
          <div className="h-80">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={reportData?.sessionsOverTime?.sessions?.map((s, i) => ({
                  date: format(new Date(s.date), 'MMM d'),
                  sessions: s.value,
                  users: reportData?.sessionsOverTime?.users?.[i]?.value ?? 0,
                })) ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sessions"
                    name="Sessions"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="users"
                    name="Users"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Top Pages</h2>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Views</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(reportData?.topPages ?? []).slice(0, 8).map((page, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900 truncate max-w-[200px]" title={page.pagePath}>
                          {page.pageTitle || page.pagePath}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500 text-right">
                          {formatNumber(page.screenPageViews)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500 text-right">
                          {formatDuration(page.avgTimeOnPage)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Traffic Sources */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Traffic Sources</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(reportData?.trafficSources ?? []).slice(0, 6).map(s => ({
                      source: `${s.source}/${s.medium}`,
                      sessions: s.sessions,
                    }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="source"
                      tick={{ fontSize: 11 }}
                      width={120}
                    />
                    <Tooltip />
                    <Bar dataKey="sessions" name="Sessions" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Events */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Top Events</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(reportData?.topEvents ?? []).slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="eventName"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="eventCount" name="Count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Device Breakdown</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64 flex items-center justify-center">
              {isLoading ? (
                <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={(reportData?.deviceBreakdown ?? []).map(d => ({
                        name: d.deviceCategory,
                        value: d.sessions,
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(reportData?.deviceBreakdown ?? []).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Geographic Data - Top Countries */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Top Countries</h2>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {(reportData?.geoData ?? []).slice(0, 12).map((geo, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-gray-900">{geo.country}</p>
                  <p className="text-lg font-bold text-brand">{formatNumber(geo.sessions)}</p>
                  <p className="text-xs text-gray-500">sessions</p>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Detailed Geographic Data - Country, State, City */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold">Visitor Locations</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">Detailed breakdown by country, state/region, and city</p>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State/Region</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(reportData?.detailedGeoData ?? []).slice(0, 25).map((geo, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {geo.country || '(not set)'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {geo.region || '(not set)'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {geo.city || '(not set)'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap font-medium">
                        {formatNumber(geo.sessions)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">
                        {formatNumber(geo.users)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(reportData?.detailedGeoData ?? []).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No geographic data available for this period
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
